import React from 'react';
import cx from 'classnames';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import Button from '../../../../../components/Button';
import IconButton from '../../../../../components/IconButton';
import Input from '../../../../../components/Input';
import styles from './OptionManagement.module.scss';

interface OptionManagementProps {
    fieldType: 'select' | 'radio' | 'checkbox';
    options: string;
    fieldIndex: number;
    onUpdateOption: (fieldIndex: number, optionIndex: number, value: string) => void;
    onAddOption: (fieldIndex: number) => void;
    onRemoveOption: (fieldIndex: number, optionIndex: number) => void;
    onCleanupEmptyOptions: (fieldIndex: number) => void;
}

const OptionManagement: React.FC<OptionManagementProps> = ({
    fieldType,
    options,
    fieldIndex,
    onUpdateOption,
    onAddOption,
    onRemoveOption,
    onCleanupEmptyOptions
}) => {
    const optionsList = (options !== null && options !== undefined) ? options.split('\n') : [];

    if (optionsList.length === 0) {
        return (
            <div className={cx(styles['options-container'], 'zr-mb-3')}>
                <div className={cx(styles['options-header'], 'zr-mb-3')}>
                    <h6>
                        {fieldType === 'select' && 'Dropdown-Optionen'}
                        {fieldType === 'radio' && 'Radio Button Optionen'}
                        {fieldType === 'checkbox' && 'Checkbox Optionen'}
                    </h6>
                </div>

                <div className={cx(styles['no-options'], 'zr-mb-3')}>
                    <p>Noch keine Optionen hinzugefügt.</p>
                </div>

                <div className={cx(styles['add-option-container'])}>
                    <Button
                        hierarchy="secondary"
                        text="+ Option hinzufügen"
                        fn={() => onAddOption(fieldIndex)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={cx(styles['options-container'], 'zr-mb-3')}>
            <div className={cx(styles['options-header'], 'zr-mb-3')}>
                <h6>
                    {fieldType === 'select' && 'Dropdown-Optionen'}
                    {fieldType === 'radio' && 'Radio Button Optionen'}
                    {fieldType === 'checkbox' && 'Checkbox Optionen'}
                </h6>
            </div>

            {optionsList.map((option, optionIndex) => {
                // Count non-empty options for UI state
                const nonEmptyCount = optionsList.filter(opt => opt.trim()).length;

                return (
                    <div key={optionIndex} className={cx(styles['option-row'], 'zr-mb-2')}>
                        <div 
                            onKeyDown={(e) => {

                                if (e.key === 'Backspace') {
                                    e.stopPropagation();
                                }

                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onAddOption(fieldIndex);
                                }
                            }}
                            onBlur={() => {
                                setTimeout(() => onCleanupEmptyOptions(fieldIndex), 100);
                            }}
                        >
                            <Input
                                type="text"
                                property={`option-${optionIndex}`}
                                value={option}
                                onChange={(_, value) => onUpdateOption(fieldIndex, optionIndex, value)}
                                placeholder={`Option ${optionIndex + 1}`}
                                data-option-field={`${fieldIndex}-${optionIndex}`}
                            />
                        </div>
                        <IconButton
                            icon={faTrash}
                            color="#666666"
                            onClick={() => onRemoveOption(fieldIndex, optionIndex)}
                            title="Option entfernen"
                            classes={cx(styles['remove-option-btn'], {
                                [styles['disabled']]: nonEmptyCount <= 1
                            })}
                            style={{
                                opacity: nonEmptyCount <= 1 ? 0.5 : 1,
                                pointerEvents: nonEmptyCount <= 1 ? 'none' : 'auto'
                            }}
                        />
                    </div>
                );
            })}

            <div className={cx(styles['add-option-container'])}>
                <Button
                    hierarchy="secondary"
                    text="+ Option hinzufügen"
                    fn={() => onAddOption(fieldIndex)}
                />
            </div>
        </div>
    );
};

export default OptionManagement; 